import type { PortfolioReport } from '@interfaces/portfolioReport';
import { UserContext } from '@providers/userContextProvider';
import { useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { usePermission } from '../../../../hooks/usePermission';
import type { BulkRebalanceRun } from '../types/run.types';
import type {
  ReviewTradeCounts,
  ReviewTradePortfolioGroup,
  ReviewTradeRow,
} from '../types/tradeReview.types';
import {
  canProceedFromTradeReview,
  resolveReviewEvaluateProcess,
  resolveTradeReviewProceedDisabledReason,
  shouldShowTradeReview,
} from '../utils/tradeReview/tradeReviewPresentation';
import { useReviewTradeEvaluate } from './useReviewTradeEvaluate';
import { useReviewTradePaging } from './useReviewTradePaging';

export type UseBulkRebalanceTradeReviewParams = {
  runId: string | undefined;
  run: BulkRebalanceRun | null;
  succeededCount: number;
  reports: PortfolioReport[];
  runCanProceed: boolean;
};

export type UseBulkRebalanceTradeReviewResult = {
  showReview: boolean;
  tradesLoading: boolean;
  tradesFullyLoaded: boolean;
  evaluating: boolean;
  cancelling: boolean;
  verifying: boolean;
  rows: ReviewTradeRow[];
  groups: ReviewTradePortfolioGroup[];
  counts: ReviewTradeCounts;
  totalCount: number;
  evaluated: boolean;
  requireApproval: boolean;
  canCancel: boolean;
  canApprove: boolean;
  canEvaluate: boolean;
  proceedDisabledReason?: string;
  showProceedButton: boolean;
  onCancelTrades: (ids: string[]) => Promise<void>;
  onApproveTrades: (ids: string[]) => Promise<void>;
};

export const useBulkRebalanceTradeReview = ({
  runId,
  run,
  succeededCount,
  reports,
  runCanProceed,
}: UseBulkRebalanceTradeReviewParams): UseBulkRebalanceTradeReviewResult => {
  const { t } = useTranslation(['bulkRebalance']);
  const { activeOrganization } = useContext(UserContext);
  const canWriteSubTradeRequest = usePermission('write:sub_trade_request');
  const canTransitionSubTradeRequest = usePermission(
    'transition:sub_trade_request',
  );
  const canWriteVerification = usePermission(
    'write:sub_trade_request_verification',
  );

  const showReview = shouldShowTradeReview(run, succeededCount);
  const requireApproval =
    activeOrganization?.requireSubTradeRequestApproval ?? false;
  const process = resolveReviewEvaluateProcess(
    activeOrganization?.bulkTradingProcess,
  );
  const organizationId = activeOrganization?.id;

  const paging = useReviewTradePaging({
    showReview,
    runId,
    organizationId,
  });

  const evaluation = useReviewTradeEvaluate({
    runId,
    reports,
    trades: paging.trades,
    tradesSettled: paging.tradesSettled,
    usableBulkTradeRequestId: paging.usableBulkTradeRequestId,
    organizationId,
    requireApproval,
    process,
    canWriteSubTradeRequest,
    refetchTrades: paging.refetchTrades,
  });

  const proceedDisabledReason = resolveTradeReviewProceedDisabledReason(t, {
    showReview,
    tradesSettled: paging.tradesSettled,
    evaluated: evaluation.evaluated,
    evaluating: evaluation.evaluating,
    canEvaluate: evaluation.canEvaluate,
    canWriteSubTradeRequest,
    counts: evaluation.counts,
    requireApproval,
  });
  const showProceedButton = canProceedFromTradeReview({
    runCanProceed,
    tradesSettled: paging.tradesSettled && paging.tradesFullyFetched,
    evaluated: evaluation.evaluated,
    evaluating: evaluation.evaluating,
    actionableCount: evaluation.counts.actionable,
    requireApproval,
    unverifiedActionableCount: evaluation.counts.unverifiedActionable,
  });
  const tradesFullyLoaded =
    !showReview ||
    paging.noReviewBulkTradeRequest ||
    (paging.tradesQueryReady &&
      paging.tradesFullyFetched &&
      !paging.pagingTrades &&
      !paging.tradesQueryLoading);

  return {
    showReview,
    tradesLoading:
      showReview &&
      (paging.bulkTradeRequestLoading ||
        (!!paging.bulkTradeRequestId &&
          (paging.tradesQueryLoading || paging.pagingTrades))) &&
      evaluation.rows.length === 0,
    tradesFullyLoaded,
    evaluating: evaluation.evaluating,
    cancelling: evaluation.cancelling,
    verifying: evaluation.verifying,
    rows: evaluation.rows,
    groups: evaluation.groups,
    counts: evaluation.counts,
    totalCount: paging.totalCount,
    evaluated: evaluation.evaluated,
    requireApproval,
    canCancel: canTransitionSubTradeRequest,
    canApprove: canWriteVerification && requireApproval,
    canEvaluate: evaluation.canEvaluate,
    proceedDisabledReason,
    showProceedButton,
    onCancelTrades: evaluation.onCancelTrades,
    onApproveTrades: evaluation.onApproveTrades,
  };
};
